import { enumerable } from '../utils/utils';
import { Tree } from '../tree';
import { TreeNode, ITreeNode } from '../tree/node';

export enum SynthesisTreeNodeType {
  Compound = 'Compound',
  Reaction = 'Reaction',
}

interface SynthesisTreeNode<T> extends ITreeNode<T> {
  isCompound(): boolean;
  isReaction(): boolean;
  getType(): SynthesisTreeNodeType;
}

interface SynthesisTree<T, U extends SynthesisTreeNode<T>> {
  getCompoundById(id: string): U | undefined;
  getReactionById(id: string): U | undefined;
}

interface NavigableTreeNode {
  select(): void;
  unselect(): void;
  isSelected(): boolean;
}

interface NavigableTree<T> {
  getSelectedTree(): NavigableTree<T>;
}

export class InteractiveTreeNode<T, U extends InteractiveTreeNode<T, U>>
  extends TreeNode<ITreeNode<T>>
  implements SynthesisTreeNode<T>, NavigableTreeNode
{
  private type: SynthesisTreeNodeType;
  private selected: boolean = false;
  @enumerable(false)
  protected override context!: IInteractiveTree<T, U>;

  constructor(id: string, type: SynthesisTreeNodeType, data?: any) {
    super(id, data);
    this.type = type;
  }
  public isCompound(): boolean {
    return this.type == SynthesisTreeNodeType.Compound;
  }

  public isReaction(): boolean {
    return this.type == SynthesisTreeNodeType.Reaction;
  }

  public override addChild(node: InteractiveTreeNode<T, U>): void {
    if (node.getType() === this.getType()) {
      // type guards?
      throw new Error(
        `Node (id: ${this.id}) of type ${this.getType()} can't be a parent of another ${node.getType()} (id: ${
          node.id
        })`
      );
    }
    super.addChild(node);
  }

  public getType(): SynthesisTreeNodeType {
    return this.type;
  }

  public select() {
    this.selected = true;
  }

  public unselect() {
    this.selected = false;
  }

  public isSelected() {
    return this.selected;
  }

  public expand() {
    this.context.onExpand(this.id);
  }
  public collapse() {
    this.context.onCollapse(this.id);
  }
}

export interface IInteractiveTree<T, N extends InteractiveTreeNode<T, N>>
  extends SynthesisTree<T, SynthesisTreeNode<T>>,
    NavigableTree<T>,
    Tree<T> {
  nodes: Record<string, N>;
  onExpand: (nodeId: string) => void;
  onCollapse: (nodeId: string) => void;
}

export interface IInteractiveTreeNode<T> extends SynthesisTreeNode<T>, NavigableTreeNode, ITreeNode<T> {}

export class InteractiveTree<T, N extends InteractiveTreeNode<T, N>>
  extends Tree<T>
  implements IInteractiveTree<T, N>, Iterable<number>
{
  @enumerable(false)
  public override nodes: Record<string, N> = {};
  public onExpand: (nodeId: string) => void;
  public onCollapse: (nodeId: string) => void;

  constructor(onExpand: (nodeId: string) => void = () => {}, onCollapse: (nodeId: string) => void = () => {}) {
    super();
    this.onExpand = onExpand;
    this.onCollapse = onCollapse;
  }

  public override setRootNode(node: N) {
    if (!node.isCompound()) {
      // type guards?
      throw new Error(`node of type ${node.getType()} cannot be a root of synthesis tree`);
    }
    super.setRootNode(node);
  }

  public getCompoundById(id: string): InteractiveTreeNode<T, N> | undefined {
    const node = this.getNodeById(id);
    if (node instanceof InteractiveTreeNode && node.isCompound()) {
      return node;
    }
    return;
  }

  public getReactionById(id: string): InteractiveTreeNode<T, N> | undefined {
    const node = this.getNodeById(id);
    if (node instanceof InteractiveTreeNode && node.isReaction()) {
      return node;
    }
    return;
  }

  public getSelectedNodes(): InteractiveTreeNode<T, N>[] {
    return Object.values(this.nodes).filter(n => n.isSelected());
  }

  public getSelectedTree() {
    // unimplemented!
    // TODO: implement tree iterators?
    // return this;

    const tree = new InteractiveTree();
    // @ts-ignore
    for (let originalNode of this) {
      // TSLint bug?
      if (originalNode.isSelected() && (!originalNode.hasParent() || originalNode.getParent()?.isSelected())) {
        const nodeId = originalNode.id;
        const type = originalNode.getType();
        const data = originalNode.getData();
        let node = tree.getNodeById(nodeId) as InteractiveTreeNode<T, N>;
        if (!node) {
          node = new InteractiveTreeNode(nodeId, type, data);
          if (originalNode.isRoot()) {
            tree.setRootNode(node);
          }
        }
        // node. // recreate parent<->child relationship
      }
    }

    return tree;
  }

  public chooseReactionPathLeadingTo(reactionId: string) {
    const reaction = this.getReactionById(reactionId);
    if (!reaction) {
      throw new Error(`invalid id ${reactionId}`);
    }

    const previouslySelectedReactions = this.getSelectedNodes().filter(node => node.isReaction());
    previouslySelectedReactions.forEach(node => node.unselect());

    reaction.select();

    const ancestors = reaction.getAncestors() as InteractiveTreeNode<T, N>[];
    ancestors
      .filter(node => node.isReaction())
      .forEach(node => {
        node.select();
        const compounds = node.getChildren() as InteractiveTreeNode<T, N>[];
        compounds.forEach(c => c.select());
      });

    const selectedCompounds = this.getSelectedNodes().filter(node => node.isCompound());
    const orphanCompounds = selectedCompounds.filter(compound => {
      const parent = compound.getParent() as InteractiveTreeNode<T, N>;
      return parent && !parent.isSelected();
    });
    orphanCompounds.forEach(orphan => orphan.unselect());

    const root = this.getRootNode() as InteractiveTreeNode<T, N>;
    root.select();
  }

  private convergeSelection() {
    const selected = this.getSelectedNodes();
    const root = this.getRootNode() as InteractiveTreeNode<T, N>;
    if (root) {
      root.select();
    }
    selected.forEach(node => {
      if (node.isReaction()) {
        const children = node.getChildren() as InteractiveTreeNode<T, N>[];
        children.forEach(compound => compound.select());
        // TODO: apply default selection downstream...
        // TODO: order may matter
      } else {
        if (node.hasParent()) {
          const parent = node.getParent() as InteractiveTreeNode<T, N>;
          if (!parent.isSelected()) {
            node.unselect();
          }
        }
      }
    });
  }

  public selectPath(reactionId: string) {
    const reaction = this.getReactionById(reactionId);
    if (!reaction) {
      throw new Error(`invalid id ${reactionId}`);
    }

    let node = reaction;
    do {
      if (node.isReaction()) {
        node.select();
        this.setDefaultSelection(node, false);
        const siblings = node.getSiblings() as InteractiveTreeNode<T, N>[];
        siblings.forEach(reaction => {
          this.clearSelection(reaction);
          // reaction.unselect();
          // const descendants = reaction.getDescendants() as InteractiveTreeNode<T, N>[];
          // descendants.forEach(node => this.clearSelection(node));
        });
      } else {
      }
      node = node.getParent() as InteractiveTreeNode<T, N>;
    } while (node.hasParent());
    this.convergeSelection();
  }

  public clearSelection(root = this.getRootNode() as InteractiveTreeNode<T, N>) {
    root.unselect();
    const descendants = root.getDescendants() as InteractiveTreeNode<T, N>[];
    const selected = descendants.filter(node => node.isSelected());
    selected.forEach(n => n.unselect());
  }

  public setDefaultSelection(root = this.getRootNode() as InteractiveTreeNode<T, N>, override = true) {
    if (override) {
      this.clearSelection(root);
    }
    root.select();
    const descendants = root.getDescendants() as InteractiveTreeNode<T, N>[];
    descendants.forEach(n => {
      if (
        override ||
        (!override && !(n.getSiblings() as InteractiveTreeNode<T, N>[]).some(node => node.isSelected()))
      ) {
        const isInSelectedPath = n.getParent()?.hasParent()
          ? (n.getParent().getParent() as InteractiveTreeNode<T, N>).isSelected()
          : true;
        if (n.isReaction() && n.isFirstChild() && isInSelectedPath) {
          n.select();
        }
      }
    });
    this.convergeSelection();
  }

  public modifySelection(selectedNodes: string[]) {
    const selectedReactionIds = selectedNodes.filter(rid => {
      return (this.getNodeById(rid) as InteractiveTreeNode<T, N>).isReaction();
    });
    const selectedAncestors = selectedReactionIds.reduce((acc, currentReactionId) => {
      const ancestors = (this.getReactionById(currentReactionId)?.getAncestors() as InteractiveTreeNode<T, N>[])
        .filter(node => node.isReaction())
        .map(node => node.id);
      return [...acc, ...ancestors];
    }, [] as string[]);

    const allSelected = [...selectedAncestors, ...selectedReactionIds];

    const currentlySelected = this.getSelectedNodes()
      .filter(node => node.isReaction())
      .map(node => node.id);

    // TODO: clean up orphans, we're currently calling selectPath for orphaned (but not-directly-conflicting)
    // reactions (conflict happens in one of ancestors)
    const nonConflictingCurrentlySelectedReactionIds = currentlySelected.filter(rid => {
      return !this.getReactionById(rid)
        ?.getSiblings()
        .some(sibling => {
          return allSelected.includes(sibling.id);
        });
    });
    const withoutAncestors = nonConflictingCurrentlySelectedReactionIds.filter(rid => {
      const parentReaction = this.getReactionById(rid)?.getParent()?.getParent() as InteractiveTreeNode<T, N>;
      if (!parentReaction) return true;
      return selectedAncestors.includes(parentReaction.id);
    });

    const uniqueReactionIds = [...new Set([...allSelected, ...withoutAncestors])];

    uniqueReactionIds
      .map(rid => this.getReactionById(rid) as InteractiveTreeNode<T, N>)
      .sort((a, b) => {
        return a.getDepth() - b.getDepth();
      })
      .map(node => node.id)
      .forEach(rid => {
        this.selectPath(rid);
      });
    (this.getRootNode() as InteractiveTreeNode<T, N>).select();
  }
}
